import React from 'react';
import Icon from '../../../components/AppIcon';

const CompanyOverview = ({ partner }) => {
  const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-16 py-16 border-b border-slate-100 last:border-0">
      <div className="p-10 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg">
        <Icon name={icon} size={20} className="text-primary" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-500 mb-4">{label}</div>
        <div className="text-base font-semibold text-slate-900">{value || '—'}</div>
      </div>
    </div>
  );

  const legalName = partner?.legal_name || '—';
  const brandName = partner?.brand_name || '—';
  const taxId = partner?.tax_id || '—';
  const yearEstablished = partner?.year_established || '—';
  const country = partner?.country || '—';
  const website = partner?.website || null;
  const ownershipStructure = partner?.ownership_structure || '—';
  const authorizedSignatory = partner?.authorized_signatory || '—';
  const partnerType = partner?.partner_type || '—';
  const description = partner?.description || `${legalName} is a registered partner in the PartnerHub network.`;

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32">
      <div className="flex items-center gap-12 mb-24">
        <div className="p-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
          <Icon name="Building2" size={24} className="text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Company Overview</h2>
          <p className="text-sm text-slate-600">Legal information and business profile</p>
        </div>
      </div>

      {/* Company Description */}
      <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl p-24 mb-32">
        <h3 className="text-lg font-semibold text-slate-900 mb-12">About the Company</h3>
        <p className="text-slate-700 leading-relaxed">{description}</p>
      </div>

      {/* Legal Information */}
      <div className="mb-32">
        <h3 className="text-lg font-semibold text-slate-900 mb-16 flex items-center gap-8">
          <Icon name="Scale" size={20} className="text-primary" />
          Legal Information
        </h3>
        <div className="space-y-0">
          <InfoRow icon="FileText" label="Legal Name" value={legalName} />
          <InfoRow icon="Tag" label="Brand Name" value={brandName} />
          <InfoRow icon="CreditCard" label="Tax ID" value={taxId} />
          <InfoRow icon="User" label="Authorized Signatory" value={authorizedSignatory} />
        </div>
      </div>

      {/* Business Details */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-16 flex items-center gap-8">
          <Icon name="Briefcase" size={20} className="text-primary" />
          Business Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-32">
          <InfoRow icon="Calendar" label="Year Established" value={yearEstablished} />
          <InfoRow icon="Globe" label="Country" value={country} />
          <InfoRow icon="Layers" label="Partner Type" value={partnerType} />
          <InfoRow icon="Building" label="Ownership Structure" value={ownershipStructure} />
        </div>
        {website && (
          <div className="mt-16 pt-16 border-t border-slate-100">
            <div className="flex items-center gap-12">
              <Icon name="Link" size={18} className="text-slate-400" />
              <a
                href={website?.startsWith('http') ? website : `https://${website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-secondary font-medium transition-smooth"
              >
                {website}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyOverview;